<template>
  <component :is="elementType">
    <slot name="header"></slot>
    <slot v-if="isLoading" name="loading"></slot>
    <template v-else-if="collection && collection.length > 0">
      <slot name="collection" :collection="collection" :isLoading="isLoading">
        <template v-for="(item, index) in collection">
          <slot name="element" :item="item" :index="index"></slot>
        </template>
      </slot>
    </template>
    <template v-else>
      <slot name="empty">
        <div class="empty">
          <div class="empty-icon">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="icon"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              stroke-width="2"
              stroke="currentColor"
              fill="none"
              stroke-linecap="round"
              stroke-linejoin="round">
              <path stroke="none" d="M0 0h24v24H0z" fill="none" />
              <circle cx="12" cy="12" r="9" />
              <line x1="9" y1="10" x2="9.01" y2="10" />
              <line x1="15" y1="10" x2="15.01" y2="10" />
              <path d="M9.5 15.25a3.5 3.5 0 0 1 5 0" />
            </svg>
          </div>
          <p class="empty-title">{{ emptyPageTitle }}</p>
          <p class="empty-subtitle text-muted">{{ emptyPageSubtitle }}</p>
        </div>
      </slot>
    </template>

    <slot
      v-if="showFooter"
      name="footer"
      :page="page"
      :totalCount="totalCount"
      :startRecordIndex="startRecordIndex"
      :endRecordIndex="endRecordIndex"
      :filterCount="filterCount"
      :pageCount="pageCount"
      :changePage="changePage"
      :refresh="refresh">
      <div :class="footerClass">
        <p class="m-0 text-muted">
          <small>
            Showing {{ startRecordIndex }} - {{ endRecordIndex }} of <strong>{{ filterCount }}</strong> entries
            (filtered from <strong>{{ totalCount }}</strong> total entries)
          </small>
        </p>
        <li-pagination
          v-model="page"
          :page-count="pageCount"
          class="m-0 ms-auto"
          @change="refresh"
          :labels="{ prev: $t('orders.pagination.prev'), next: $t('orders.pagination.next') }" />
      </div>
    </slot>
  </component>
</template>

<script setup lang="ts">
import { Pagination as LiPagination } from '@prospective/lithium';
import { request } from '@/api/client';
import { debounce } from '@prospective/lithium';
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useAutopilotList, type AutopilotListFilter } from '@/composables/useAutopilotList';

export interface Options {
  noLoadingState?: boolean;
}

const props = withDefaults(
  defineProps<{
    elementType?: string;
    footerClass?: string;
    endpoint: string;
    searchQuery?: string;
    resolve?: string;
    order?: string;
    orderDir?: string;
    emptyPageTitle?: string;
    emptyPageSubtitle?: string;
    pageSize?: number;
    parameters?: Record<string, any>;
    resetPage?: boolean;
    showFooter?: boolean;
    transformData?: (data: any) => any;
    autopilotKey?: string;
    autopilotDescription?: string;
    autopilotTransformer?: (item: any) => string | Record<string, any>;
    autopilotLink?: (item: any) => string;
    autopilotFilters?: () => Record<string, AutopilotListFilter>;
  }>(),
  {
    elementType: 'div',
    footerClass: 'remote-list-view-footer d-flex align-items-center',
    searchQuery: '',
    resolve: '',
    order: 'id',
    orderDir: 'asc',
    emptyPageTitle: 'No results found',
    emptyPageSubtitle: "Try adjusting your search or filter to find what you're looking for.",
    pageSize: 20,
    parameters: () => ({}),
    resetPage: false,
    showFooter: true,
    transformData: (data: any) => data,
  }
);
const emit = defineEmits<{
  (e: 'before-fetch'): void;
  (e: 'page-resetted'): void;
  (e: 'after-fetch', data: any): void;
  (e: 'after-fetch-meta', data: { totalCount: number; filterCount: number }): void;
}>();

const isLoading = ref(true);
const page = ref(1);
const collection = ref<{ [key: string]: any }[]>([]);
const totalCount = ref(0);
const filterCount = ref(0);

let fetchDebouncer: any = null;

onMounted(() => {
  fetch();
  fetchDebouncer = debounce<typeof fetch>(fetch, 300);
});

window.addEventListener('keyup', handleKeypress);
onBeforeUnmount(() => {
  window.removeEventListener('keyup', handleKeypress);
});

/* -------------------------------- COMPUTED -------------------------------- */

const pageCount = computed(() => {
  return Math.max(1, Math.ceil(filterCount.value / props.pageSize));
});
const startRecordIndex = computed(() => {
  return (page.value - 1) * props.pageSize + 1;
});
const endRecordIndex = computed(() => {
  return (page.value - 1) * props.pageSize + props.pageSize;
});

/* ---------------------------------- WATCH --------------------------------- */
// shared watcher so a parent flipping sort alongside a search coalesces into one request.
watch([() => props.searchQuery, () => props.order, () => props.orderDir], (_curr, prev) => {
  const searchChanged = prev?.[0] !== props.searchQuery;
  refresh({ canResetPage: true, debounced: searchChanged });
});

// filter changes (tab, chips, date range, …) are discrete user actions — fire
// immediately so pagination and filter clicks don't feel laggy.
watch(
  () => props.parameters,
  () => {
    refresh({ canResetPage: true });
  },
  { deep: true }
);

/* --------------------------------- METHODS -------------------------------- */

function refresh(options: Record<string, any> | number | undefined = undefined) {
  isLoading.value = true;
  page.value = Math.max(1, page.value);
  const debounced = typeof options === 'object' && options !== null && (options as any).debounced === true;
  if (debounced) {
    fetchDebouncer(options);
  } else {
    fetch(options as Options);
  }
}

function fetch(options: Options = {}) {
  if (!props.endpoint) {
    console.error('Cannot fetch "RemoteListView" without a valid endpoint.');
    return;
  }

  if ((options.noLoadingState ?? false) === false) {
    isLoading.value = true;
  }

  // clone in order to modify, there has to be a better way...
  const requestParameters = JSON.parse(JSON.stringify(props.parameters));

  // set some required request params
  requestParameters.count = 1;

  // reset the page on filtering and searching
  if (props.resetPage && (options as any)?.canResetPage) {
    page.value = 1;
  }
  requestParameters.page = page.value - 1;
  requestParameters.page_size = props.pageSize;
  requestParameters.order = props.order ?? 'id';
  requestParameters.order_dir = props.orderDir;
  requestParameters.search = props.searchQuery;
  if (props.resolve) requestParameters.resolve = props.resolve;

  emit('before-fetch');

  // run the request
  request('GET', props.endpoint, requestParameters)
    .then((response) => {
      collection.value = props.transformData(response.data);
      totalCount.value = response?.total_count ?? null;
      filterCount.value = response?.filter_count ?? null;
      emit('after-fetch', collection.value);
      emit('after-fetch-meta', { totalCount: totalCount.value, filterCount: filterCount.value });
    })
    .catch((e) => {
      console.error(e);
      // new reference so holdUntilReload releases; keep rows so a transient error does not blank the UI
      collection.value = collection.value ? collection.value.slice() : [];
      emit('after-fetch', collection.value);
    })
    .finally(() => {
      isLoading.value = false;
      emit('page-resetted');
    });
}

function handleKeypress(event: KeyboardEvent) {
  if (event.key === 'ArrowRight') {
    page.value++;
    refresh();
  } else if (event.key === 'ArrowLeft') {
    page.value--;
    refresh();
  }
}
function changePage(newPage: number) {
  page.value = newPage;
  refresh();
}

useAutopilotList({
  key: () => props.autopilotKey,
  description: () => props.autopilotDescription,
  transformer: props.autopilotTransformer,
  link: props.autopilotLink,
  collection,
  page,
  pageCount,
  filterCount,
  totalCount,
  paginate: changePage,
  filters: props.autopilotFilters,
});

defineExpose({ refresh, collection, isLoading, page, pageCount, filterCount, changePage });
</script>

<style>
.no-data {
  padding: 20px;
  font-size: 16px;
}
</style>
